import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Award, Star, Trophy, Medal, Crown, Sparkles, Gift, Heart,
  TrendingUp, Users, Brain, Calendar, Share2, Download, ArrowLeft,
  CheckCircle, Lock
} from 'lucide-react';
import PrincipalNav from '../components/PrincipalNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ChildCelebration = () => {
  const navigate = useNavigate();
  const { childId } = useParams();
  
  const [badges, setBadges] = useState(null);
  const [report, setReport] = useState(null);
  const [milestones, setMilestones] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('badges');

  const getToken = () => {
    // Support both parent and principal access
    return localStorage.getItem('principal_token') || localStorage.getItem('parent_token');
  };

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/principal/login');
      return;
    }
    loadData();
  }, [navigate, childId]);

  const loadData = async () => {
    try {
      const token = getToken();
      
      const [badgesRes, reportRes, milestonesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/child/${childId}/badges`, { params: { token } }),
        axios.get(`${BACKEND_URL}/api/child/${childId}/celebration-report`, { params: { token } }),
        axios.get(`${BACKEND_URL}/api/child/${childId}/milestones`, { params: { token } })
      ]);
      
      setBadges(badgesRes.data);
      setReport(reportRes.data);
      setMilestones(milestonesRes.data);
    } catch (error) {
      console.error('Error loading celebration data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkBadges = async () => {
    try {
      const token = getToken();
      const response = await axios.post(`${BACKEND_URL}/api/child/${childId}/check-badges`, null, {
        params: { token }
      });
      
      if (response.data.new_badges?.length > 0) {
        alert(`🎉 ${response.data.count} new badge(s) earned!`);
        loadData();
      } else {
        alert('No new badges earned yet. Keep going!');
      }
    } catch (error) {
      console.error('Error checking badges:', error);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      communication: Users,
      emotional: Heart,
      social: Users,
      growth: TrendingUp,
      creativity: Sparkles,
      consistency: Calendar,
      milestone: Trophy,
      other: Star
    };
    return icons[category] || Star;
  };

  const getBadgeIcon = (icon) => {
    const icons = {
      baby: '👶',
      flame: '🔥',
      trophy: '🏆',
      sun: '☀️',
      'book-open': '📖',
      'heart-handshake': '🤝',
      lightbulb: '💡',
      palette: '🎨',
      award: '🏅',
      medal: '🥇'
    };
    return icons[icon] || '⭐';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PrincipalNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-pink-50" data-testid="child-celebration-page">
      <PrincipalNav />
      
      <div className="pt-16 pb-8 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Trophy className="w-7 h-7 text-yellow-500" />
                  {badges?.child_name}&apos;s Achievements
                </h1>
                <p className="text-gray-600">Celebrating growth and milestones</p>
              </div>
            </div>
            <Button onClick={checkBadges} className="bg-yellow-500 hover:bg-yellow-600">
              <Sparkles className="w-4 h-4 mr-2" />
              Check for New Badges
            </Button>
          </div>

          {/* Points Summary */}
          <Card className="mb-6 bg-gradient-to-r from-yellow-400 to-orange-500 text-white shadow-xl">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-yellow-100 text-sm">Total Points Earned</p>
                  <p className="text-5xl font-bold">{badges?.total_points || 0}</p>
                </div>
                <div className="text-right">
                  <p className="text-yellow-100 text-sm">Badges Collected</p>
                  <p className="text-4xl font-bold">{badges?.badges_earned?.length || 0}</p>
                </div>
                <Crown className="w-24 h-24 text-yellow-200 opacity-50" />
              </div>
            </CardContent>
          </Card>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            {['badges', 'milestones', 'report'].map(tab => (
              <Button
                key={tab}
                variant={activeTab === tab ? 'default' : 'outline'}
                onClick={() => setActiveTab(tab)}
                className={activeTab === tab ? 'bg-yellow-500 hover:bg-yellow-600' : ''}
              >
                {tab === 'badges' && <Award className="w-4 h-4 mr-2" />}
                {tab === 'milestones' && <Trophy className="w-4 h-4 mr-2" />}
                {tab === 'report' && <Gift className="w-4 h-4 mr-2" />}
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </Button>
            ))}
          </div>

          {/* Badges Tab */}
          {activeTab === 'badges' && (
            <div className="space-y-6">
              {/* Earned Badges */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-500" />
                    Earned Badges ({badges?.badges_earned?.length || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {badges?.badges_earned?.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {badges.badges_earned.map((badge, idx) => (
                        <div 
                          key={idx}
                          className="p-4 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl border border-yellow-200 text-center hover:shadow-lg transition-shadow"
                        >
                          <div className="text-4xl mb-2">{getBadgeIcon(badge.icon)}</div>
                          <h4 className="font-bold text-gray-900">{badge.name}</h4>
                          <p className="text-xs text-gray-500 mt-1">{badge.description}</p>
                          <div className="mt-2 flex items-center justify-center gap-1">
                            <Star className="w-3 h-3 text-yellow-500" />
                            <span className="text-sm font-semibold text-yellow-600">+{badge.points}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Medal className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No badges earned yet. Keep participating!</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Available Badges */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="w-5 h-5 text-gray-400" />
                    Badges to Unlock ({badges?.available_badges?.length || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {badges?.available_badges?.map((badge, idx) => (
                      <div 
                        key={idx}
                        className="p-4 bg-gray-100 rounded-xl border border-gray-200 text-center opacity-60"
                      >
                        <div className="text-4xl mb-2 grayscale">{getBadgeIcon(badge.icon)}</div>
                        <h4 className="font-bold text-gray-700">{badge.name}</h4>
                        <p className="text-xs text-gray-500 mt-1">{badge.description}</p>
                        <div className="mt-2 flex items-center justify-center gap-1">
                          <Lock className="w-3 h-3 text-gray-400" />
                          <span className="text-sm text-gray-500">+{badge.points}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Milestones Tab */}
          {activeTab === 'milestones' && milestones && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-purple-500" />
                  Session Milestones
                </CardTitle>
                <p className="text-sm text-gray-500">
                  Current: {milestones.current_sessions} sessions completed
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {milestones.milestones?.map((milestone, idx) => (
                    <div key={idx} className="relative">
                      <div className={`flex items-center gap-4 p-4 rounded-xl ${
                        milestone.completed 
                          ? 'bg-green-50 border border-green-200' 
                          : 'bg-gray-50 border border-gray-200'
                      }`}>
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl ${
                          milestone.completed ? 'bg-green-100' : 'bg-gray-200'
                        }`}>
                          {milestone.emoji}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className={`font-bold ${milestone.completed ? 'text-green-800' : 'text-gray-700'}`}>
                              {milestone.name}
                            </h4>
                            {milestone.completed && (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            {milestone.sessions} sessions required
                          </p>
                          
                          {/* Progress bar */}
                          <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all ${
                                milestone.completed ? 'bg-green-500' : 'bg-blue-500'
                              }`}
                              style={{ width: `${milestone.progress}%` }}
                            />
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            {milestone.completed 
                              ? '✓ Completed!' 
                              : `${milestone.remaining} more to go`}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Next Milestone */}
                {milestones.next_milestone && (
                  <div className="mt-6 p-4 bg-purple-50 rounded-xl border border-purple-200">
                    <h4 className="font-bold text-purple-800 mb-2">🎯 Next Goal</h4>
                    <p className="text-purple-700">
                      Complete {milestones.next_milestone.remaining} more sessions to earn the{' '}
                      <strong>{milestones.next_milestone.name}</strong> milestone!
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Celebration Report Tab */}
          {activeTab === 'report' && report && (
            <div className="space-y-6">
              {/* Shareable Card */}
              <Card className="bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-xl">
                <CardContent className="p-8 text-center">
                  <div className="mb-4">
                    <span className="text-6xl">{report.shareable?.title?.split(' ')[0]}</span>
                  </div>
                  <h2 className="text-2xl font-bold mb-2">{report.child_name}&apos;s Celebration Report</h2>
                  <p className="text-purple-100 mb-4">{report.shareable?.subtitle}</p>
                  <div className="inline-block bg-white/20 rounded-full px-6 py-2">
                    <p className="text-lg font-semibold">{report.shareable?.key_stat}</p>
                  </div>
                  <p className="mt-4 text-lg italic">&quot;{report.shareable?.quote}&quot;</p>
                </CardContent>
              </Card>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <Calendar className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{report.summary?.total_sessions || 0}</p>
                    <p className="text-xs text-gray-500">Sessions</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Heart className="w-8 h-8 text-pink-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{report.summary?.happy_days || 0}</p>
                    <p className="text-xs text-gray-500">Happy Days</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Award className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{report.summary?.badges_earned || 0}</p>
                    <p className="text-xs text-gray-500">Badges Earned</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Star className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{report.summary?.total_points || 0}</p>
                    <p className="text-xs text-gray-500">Total Points</p>
                  </CardContent>
                </Card>
              </div>

              {/* Celebration Messages */}
              {report.celebration_messages?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-yellow-500" />
                      Celebrations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {report.celebration_messages.map((msg, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg">
                          <span className="text-xl">{msg.split(' ')[0]}</span>
                          <p className="text-gray-700">{msg.substring(msg.indexOf(' ') + 1)}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Highlights */}
              {report.highlights?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-orange-500" />
                      Session Highlights
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {report.highlights.slice(0, 5).map((highlight, idx) => (
                        <div key={idx} className="p-3 bg-orange-50 rounded-lg border-l-4 border-orange-400">
                          <p className="text-gray-700">{highlight.text}</p>
                          <p className="text-xs text-gray-500 mt-1">{highlight.date}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChildCelebration;
